import { rewriteJs } from "@rewriters/js";
import { ScramjetClient, ProxyCtx, Proxy } from "@client/index";
import { callerMeta } from "@client/caller";

// The rewritten source is "return <the function>", and it has to be evaluated by the plain
// Function constructor: evaluated by the one that was called, an AsyncFunction made an async
// wrapper that returned a Promise of the function (and a GeneratorFunction a generator), so
// `new AsyncFunction(...)` gave back no function at all. Alpine.js evaluates every
// expression that way (x-data, @click...); its components came out empty (bergfex).
function rewriteFunction<T extends string, U extends "construct" | "apply">(
	ctx: ProxyCtx<T, U>,
	client: ScramjetClient,
	evaluate: (code: string) => () => unknown
) {
	const stringifiedFunction = ctx.call().toString();

	// TODO: also check if the function comes from a weird realm. if so we need to completely block it or do something else weird
	// not much point rewriting the javascript if it's executing in the top level

	const content = rewriteJs(
		`return ${stringifiedFunction}`,
		"(function proxy)",
		client.context,
		callerMeta(client)
	);
	ctx.return(evaluate(content)());
}

export default function (client: ScramjetClient, _self: Self) {
	const RawFunction = client.natives.call(
		"eval",
		null,
		"(function () {})"
	).constructor;
	const evaluate = (code: string) => RawFunction(code);
	const handler: Proxy = {
		apply(ctx: ProxyCtx) {
			rewriteFunction(ctx, client, evaluate);
		},
		construct(ctx) {
			rewriteFunction(ctx, client, evaluate);
		},
	};

	client.Proxy("Function", handler);

	const RawAsyncFunction = client.natives.call(
		"eval",
		null,
		"(async function () {})"
	).constructor;
	const RawGeneratorFunction = client.natives.call(
		"eval",
		null,
		"(function* () {})"
	).constructor;
	const RawAsyncGeneratorFunction = client.natives.call(
		"eval",
		null,
		"(async function* () {})"
	).constructor;

	client.RawProxy(RawFunction.prototype, "constructor", handler);
	client.RawProxy(RawAsyncFunction.prototype, "constructor", handler);
	client.RawProxy(RawGeneratorFunction.prototype, "constructor", handler);
	client.RawProxy(RawAsyncGeneratorFunction.prototype, "constructor", handler);
}
